import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { Operation } from 'flow/service/changelog/types';
import type { PublicId } from 'flow/store/engine/types';
import { selectMovedThresholds, selectWorkflowDiff } from 'flow/store/redux/selectors';
import type { ReactNode } from 'react';
import { createContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

export const panes = {
  left: 'left',
  right: 'right',
} as const;

export type Pane = keyof typeof panes;

export type DiffContextValue = MergeDiffResult & { highlightFields: boolean };

export const DiffContext = createContext<DiffContextValue>({
  added: new Set<PublicId>(),
  modified: new Set<PublicId>(),
  removed: new Set<PublicId>(),
  highlightFields: true,
});

export function getDiffStatusById(diffResult: MergeDiffResult, id: string) {
  if (diffResult.added.has(id)) return Operation.Add;
  if (diffResult.modified.has(id)) return Operation.Modify;
  if (diffResult.removed.has(id)) return Operation.Remove;
  return null;
}

function DiffContextProvider({
  children,
  pane,
  highlightFields = true,
}: {
  children: ReactNode;
  pane: Pane;
  /**
   * Whether field-level diff highlighting should paint in this subtree.
   * Defaults to true so consumers that don't opt out (e.g. the outcome island)
   * keep highlighting every diff status.
   */
  highlightFields?: boolean;
}) {
  const workflowDiff = useSelector(selectWorkflowDiff);
  const movedThresholds = useSelector(selectMovedThresholds);

  const value = useMemo<DiffContextValue>(() => {
    const panedDiff = pane === 'left'
      ? {
        added: workflowDiff.added,
        modified: workflowDiff.modified,
        removed: new Set<PublicId>([...workflowDiff.removed, ...movedThresholds]),
      }
      : {
        added: new Set<PublicId>([...workflowDiff.added, ...movedThresholds]),
        modified: workflowDiff.modified,
        removed: workflowDiff.removed,
      };

    return { ...panedDiff, highlightFields };
  }, [pane, workflowDiff, movedThresholds, highlightFields]);

  return (
    <DiffContext.Provider value={value}>
      {children}
    </DiffContext.Provider>
  );
}

export default DiffContextProvider;
